/**
 * Generates a basic box plot specification for Vega-Lite based on the selected data.
 * The plot includes dynamically determined X and Y units, headers, and a tooltip.
 * The box plot displays the min, Q1, median, Q3, and max values for each X category.
 *
 * @param {Array} selectedData - The data selected from the spreadsheet, where each row represents a data point.
 * @returns {Object} - Returns the Vega-Lite specification for the box plot along with plot metadata (unitX, unitY, headerX, headerY).
 */
export const BoxPlotBasic = (selectedData) => {
  const values = []; // Array to store formatted plot data
  const xLabels = []; // Array to store X-axis labels
  let unitX;
  let unitY;
  let headerX;
  let headerY;

  /**
   * Iterates over each row of selected data to process and extract plot values, units, and headers.
   */
  selectedData.map((row, rowIndex) => {
    /**
     * Adds a new value to the `values` array for each cell in the row, processing both X and Y coordinates.
     * Ensures that empty or missing data is handled gracefully.
     */
    const newValue = () => {
      row.map((cell, index) => {
        const value = {};
        value.x = xLabels[index] || index; // Assign X-labels or use index as default
        value.y = cell
          ? Object.prototype.hasOwnProperty.call(cell, "m")
            ? parseFloat(cell.m)
            : 0
          : 0; // Y value (quantitative)
        values.push(value);
      });
    };

    /**
     * Checks and sets the unit for X and Y axes by inspecting the cell's format.
     * Uses the format string (e.g., "%" for percentages) to determine the unit.
     */
    const checkUnits = () => {
      const setFormat = (format) => {
        return format.includes("%") ? "%" : null; // Sets unit as percentage if format includes "%"
      };

      if (!unitX) {
        const format = selectedData[0][0]
          ? Object.prototype.hasOwnProperty.call(selectedData[0][0], "ct")
            ? selectedData[0][0].ct.fa
            : ""
          : "";
        unitX = setFormat(format);
      }

      if (!unitY) {
        const format = row[0]
          ? Object.prototype.hasOwnProperty.call(row[0], "ct")
            ? row[0].ct.fa
            : ""
          : "";
        unitY = setFormat(format);
      }
    };

    /**
     * Processes the first row to determine the X-axis labels or headers.
     * Subsequent rows are processed as data points for the plot.
     */
    if (rowIndex === 0 && row[0]) {
      row.map((cell) => {
        xLabels.push(cell.m); // Set X-axis labels from the first row
      });
    } else if (row[0]) {
      if (!unitX || !unitY) {
        checkUnits(); // Check units if they haven't been set
      }
      newValue(); // Process data for the row
    }
  });

  /**
   * Returns the Vega-Lite specification for the box plot.
   * The plot displays the min, Q1, median, Q3, and max values for each X category.
   */
  return {
    spec: {
      $schema: "https://vega.github.io/schema/vega-lite/v5.json",
      padding: 5,
      autosize: {
        type: "fit",
        resize: true,
        contains: "padding",
      },
      data: {
        values: values,
      },
      transform: [
        {
          aggregate: [
            { op: "min", field: "y", as: "min_y" },
            { op: "q1", field: "y", as: "q1_y" },
            { op: "median", field: "y", as: "median_y" },
            { op: "q3", field: "y", as: "q3_y" },
            { op: "max", field: "y", as: "max_y" },
          ],
          groupby: ["x"],
        },
        { calculate: "round(datum.max_y * 100) / 100", as: "Max" },
        { calculate: "round(datum.q3_y * 100) / 100", as: "Q3" },
        { calculate: "round(datum.median_y * 100) / 100", as: "Median" },
        { calculate: "round(datum.q1_y * 100) / 100", as: "Q1" },
        { calculate: "round(datum.min_y * 100) / 100", as: "Min" },
      ],
      layer: [
        {
          // Rule marks for min and max range
          mark: { type: "rule", color: "black" },
          encoding: {
            x: { field: "x", type: "nominal", axis: { title: null } },
            y: {
              field: "min_y",
              type: "quantitative",
              axis: {
                title: null,
                labelExpr: `datum.value + '${unitY ? unitY : ""}'`,
              },
            },
            y2: { field: "max_y", type: "quantitative" },
            tooltip: [
              { field: "Max", type: "nominal" },
              { field: "Q3", type: "nominal" },
              { field: "Median", type: "nominal" },
              { field: "Q1", type: "nominal" },
              { field: "Min", type: "nominal" },
            ],
          },
        },
        {
          // Bar marks for the interquartile range (Q1 to Q3)
          mark: { type: "bar", size: 30 },
          encoding: {
            x: { field: "x", type: "nominal", axis: { title: null } },
            y: { field: "q1_y", type: "quantitative", axis: { title: null } },
            y2: { field: "q3_y", type: "quantitative" },
            color: {
              field: "x",
              type: "nominal",
              legend: null,
              scale: {
                range: [
                  "#003e6b",
                  "#A63EE5",
                  "#6A984D",
                  "#008BBF",
                  "#AF7832",
                  "#BE2A27",
                  "#a63791",
                ],
              },
            },
            tooltip: [
              { field: "Max", type: "nominal" },
              { field: "Q3", type: "nominal" },
              { field: "Median", type: "nominal" },
              { field: "Q1", type: "nominal" },
              { field: "Min", type: "nominal" },
            ],
          },
        },
        {
          // Tick mark for the median value
          mark: {
            type: "tick",
            orient: "horizontal",
            color: "white",
            thickness: 2,
          },
          encoding: {
            x: { field: "x", type: "nominal", axis: { title: null } },
            y: {
              field: "median_y",
              type: "quantitative",
              axis: { title: null },
            },
            size: { value: 30 },
            tooltip: [
              { field: "Max", type: "nominal" },
              { field: "Q3", type: "nominal" },
              { field: "Median", type: "nominal" },
              { field: "Q1", type: "nominal" },
              { field: "Min", type: "nominal" },
            ],
          },
        },
        {
          // Rule mark to highlight the median
          mark: { type: "rule", color: "black", size: 5 },
          encoding: {
            x: { field: "x", type: "nominal", axis: { title: null } },
            y: {
              field: "median_y",
              type: "quantitative",
              axis: { title: null },
            },
            y2: { field: "median_y", type: "quantitative" },
          },
        },
        {
          // Horizontal rule to emphasize the median value across X-axis
          mark: { type: "rule", color: "black", size: 5 },
          encoding: {
            x: { field: "x", type: "nominal" },
            x2: { field: "x", type: "nominal", offset: 20 },
            y: {
              field: "median_y",
              type: "quantitative",
              axis: { title: null },
            },
          },
        },
      ],
    },
    unitX,
    unitY,
    headerX,
    headerY,
  };
};
